import {useMemo} from 'react';

import {PANEL_BANK_CHARTS_NAME} from '../../../../util/panelbankTypes';
import {getFilledRegex} from '../utils/regex';
import {useQuickAddKeys} from './useQuickAddKeys';

export type MultiMetricPanelsMap = Map<
  string,
  {metrics: string[]; defaultSection: string}
>;

/**
 * Given a regex pattern, returns a map of panels to add for multi-metric quick add.
 * Map key is the panel's regex pattern, and value contains matching metrics.
 */
export const useMultiMetricPanelsMap = (
  regex: string,
  isRegexValid: boolean
): MultiMetricPanelsMap => {
  const {searchedKeys: matchedKeys} = useQuickAddKeys(regex, 'number');

  return useMemo(() => {
    const map: MultiMetricPanelsMap = new Map();

    if (!regex || !isRegexValid) {
      return map;
    }

    matchedKeys.forEach(([key, spec]) => {
      const filledRegex = getFilledRegex(key, regex);

      if (!filledRegex) {
        return;
      }

      const panelToAdd = map.get(filledRegex);
      if (panelToAdd) {
        panelToAdd.metrics.push(key);
        // if panel matches metrics across multiple sections,
        // set Charts as default section
        if (panelToAdd.defaultSection !== spec.defaultSection) {
          panelToAdd.defaultSection = PANEL_BANK_CHARTS_NAME;
        }
      } else {
        map.set(filledRegex, {
          metrics: [key],
          defaultSection: spec.defaultSection,
        });
      }
    });

    return map;
  }, [regex, isRegexValid, matchedKeys]);
};
